import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, Plus } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DataTable, SplitList, type IDataColumn } from '@/design';
import type { IRole } from '../../api/role-contracts';
import { isRoleId, rolesApi } from '../../api/roles-api';
import {
  usePermissionCatalogue,
  useRoleList,
  useUsersAndRolesTabs
} from '../../components/account-roles';
import { absent } from '../../components/account-facts';
import { AdminPage, LoadError } from '../../components/admin-page';
import { ListArea, usePaneRead, wholePage } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { TEAM_PATHS, rolePath } from '../../paths';
import {
  CatalogueGate,
  RoleFields,
  roleBody,
  roleRefusal,
  type IRoleDraft,
  type RoleProblems
} from './role-form';

/**
 * Setup → Users & roles → Roles (SR-228, the role-model UI design's §7): every Team Portal role
 * survey-api holds, `GET /admin/roles` under `roles.manage` — which is also what opens the tab
 * (`AdminRoute`, the rail's "Users & roles" entry). A role opens beside the list
 * (`AdminRolesArea`), where it is edited, deleted and its history read.
 *
 * **The list is survey-api's, whole and in its order** — unpaged (§9 item 4 as built), so no
 * pager and no filter: seven seeded roles and what Veritant adds. The holders are its
 * `holderCount`, the permissions a count of the role's set; nothing here counts holders or sorts.
 *
 * **New role** is a form above the list, opened by its button: code, name, description and the
 * permissions grouped by the catalogue, each judged by survey-api — the code's pattern, a taken
 * code or name, a permission the creator does not hold (D5) — and said at the box it names. A role
 * made opens beside the list.
 */
export function AdminRolesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = useUsersAndRolesTabs();
  const [creating, setCreating] = React.useState(false);
  const { data: read, error, reload } = useRoleList(true);
  const data = read?.roles ?? null;
  const openRole = (roleId: string) =>
    navigate(rolePath(roleId), { state: { search: '', list: true } satisfies ICardLinkState });
  const deleted = (location.state as Nullable<{ deletedRole?: string }>)?.deletedRole;

  return (
    <AdminPage
      title="Team Portal roles"
      tabs={tabs}
      actions={
        !creating && (
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            New role
          </Button>
        )
      }
    >
      {deleted && (
        <Alert>
          <AlertDescription>The role {deleted} was deleted.</AlertDescription>
        </Alert>
      )}

      {creating && (
        <NewRoleCard
          onCancel={() => setCreating(false)}
          onCreated={(roleId) => {
            setCreating(false);
            if (roleId) {
              openRole(roleId);
            } else {
              void reload();
            }
          }}
          onReread={() => void reload()}
        />
      )}

      <p className="max-w-3xl text-sm text-muted-foreground">
        What a Team Portal account can open and do is the sum of the roles it holds. An account is
        given roles on its user card, under Users.
      </p>

      {error && data !== null && (
        <Alert variant="destructive">
          <AlertTitle>Could not refresh — showing the roles as last read</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DataTable
        columns={ROLE_COLUMNS}
        rows={data === null || data.length === 0 ? null : data}
        rowKey={(role) => role.roleId}
        onOpen={(role) => openRole(role.roleId)}
        state={
          data === null
            ? error
              ? { kind: 'error', content: <LoadError message={error} /> }
              : { kind: 'loading' }
            : { kind: 'empty', content: 'No Team Portal role is held yet.' }
        }
        pickerId="roles"
      />
    </AdminPage>
  );
}

/** A role's columns: what it is, who holds it, how much it gives. */
const ROLE_COLUMNS: IDataColumn<IRole>[] = [
  { id: 'name', header: 'Name', className: 'font-medium', cell: (role) => role.name },
  {
    id: 'code',
    header: 'Code',
    cell: (role) => <code className="text-xs">{role.code}</code>
  },
  {
    id: 'description',
    header: 'Description',
    optional: true,
    className: 'text-muted-foreground',
    cell: (role) => role.description ?? absent('None')
  },
  {
    id: 'holders',
    header: 'Holders',
    className: 'tabular-nums',
    cell: (role) => role.holderCount
  },
  {
    id: 'permissions',
    header: 'Permissions',
    className: 'tabular-nums',
    cell: (role) => role.permissions.length
  },
  {
    id: 'open',
    header: '',
    label: 'Open',
    headClassName: 'w-10',
    cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
  }
];

const EMPTY_DRAFT: IRoleDraft = { code: '', name: '', description: '', permissions: [] };

/** `POST /admin/roles`, its refusals at the boxes they name; a role made opens beside the list. */
function NewRoleCard({
  onCancel,
  onCreated,
  onReread
}: {
  onCancel: () => void;
  /** The role's id — `null` where the 201 named none, and the list is read again instead. */
  onCreated: (roleId: Nullable<string>) => void;
  onReread: () => void;
}) {
  const catalogue = usePermissionCatalogue(true);
  const [draft, setDraft] = React.useState<IRoleDraft>(EMPTY_DRAFT);
  const [problems, setProblems] = React.useState<RoleProblems>({});
  const [saving, setSaving] = React.useState(false);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setProblems({});
    try {
      const created = await rolesApi.createRole({ code: draft.code, ...roleBody(draft) });
      onCreated(created && isRoleId(created.roleId) ? created.roleId : null);
    } catch (createError) {
      const refusal = roleRefusal(createError, 'making the role');
      setProblems(refusal.problems);
      if (refusal.reread) {
        onReread();
        void catalogue.reload();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-new-role>
      <CardHeader>
        <CardTitle>New role</CardTitle>
        <CardDescription>
          A role made with no permission opens nothing yet. Give it to accounts on their user card.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={(event) => void create(event)} noValidate>
          <CatalogueGate catalogue={catalogue}>
            {(permissions) => (
              <RoleFields
                draft={draft}
                onDraft={setDraft}
                catalogue={permissions}
                problems={problems}
                codeEditable
                disabled={saving}
              />
            )}
          </CatalogueGate>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving || catalogue.data?.permissions == null}>
              {saving ? 'Making the role…' : 'Make the role'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Roles and a role, as one area: the list, or a role beside it. */
export function AdminRolesArea() {
  return (
    <ListArea
      param="roleId"
      list={TEAM_PATHS.roles}
      paneTitle="Roles"
      pane={(search, selected) => <RolesPane search={search} selected={selected} />}
    />
  );
}

/** The role list as the pane beside a role draws it — one page, the whole list. */
function RolesPane({ search, selected }: { search: string; selected: string }) {
  const { shown, status } = usePaneRead(search, 'No Team Portal role is held yet.', async (
    _params,
    _page,
    signal
  ) => {
    return wholePage(await rolesApi.listRoles(signal));
  });
  return (
    <SplitList
      label="Roles"
      selected={shown?.items.some((role) => role.roleId === selected) ? selected : null}
      items={
        shown?.items.map((role) => ({
          key: role.roleId,
          to: rolePath(role.roleId),
          state: { search, list: true } satisfies ICardLinkState,
          primary: role.name,
          secondary: `${role.holderCount} ${role.holderCount === 1 ? 'holder' : 'holders'}`
        })) ?? null
      }
      status={status}
    />
  );
}
